import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  output,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LanguageModel, Languages } from '@seahorse/common';
import { SelectModule } from 'primeng/select';

/**
 * The language picker for the new look: the same behaviour as `libs/ui`'s
 * `sh-language-picker` (sets the translate default language, emits the
 * choice), but on PrimeNG's `p-select` instead of ng-select, whose styles the
 * new-look stylesheet doesn't load. `sh-language-picker` stays for the old
 * look and the customer apps.
 */
@Component({
  selector: 'sh-language-select',
  changeDetection: ChangeDetectionStrategy.Default,
  imports: [CommonModule, FormsModule, TranslateModule, SelectModule],
  template: `
    <p-select
      [options]="languageOptions"
      [ngModel]="currentLanguage"
      (ngModelChange)="changeLanguage($event)"
      optionLabel="label"
      [ariaLabel]="'shared.terms.language' | translate"
      appendTo="body"
      styleClass="language-select"
    >
      <ng-template pTemplate="selectedItem" let-item>
        <span class="language-select-item"
          ><i [class]="item?.flag"></i>{{ item?.label }}</span
        >
      </ng-template>
      <ng-template pTemplate="item" let-item>
        <span class="language-select-item"
          ><i [class]="item.flag"></i>{{ item.label }}</span
        >
      </ng-template>
    </p-select>
  `,
})
export class LanguageSelectComponent {
  private readonly _translateService = inject(TranslateService);

  /** Preselect a language (value, e.g. 'nl'); defaults to the current translate language. */
  language = input<string>();
  languageChange = output<string>();

  // The list itself, not a copy: p-select must get the same array every time.
  protected readonly languageOptions = Languages;
  protected currentLanguage: LanguageModel | undefined = Languages.find(
    (x) => x.value === this._translateService.getDefaultLang()
  );

  constructor() {
    // Keep the preselection in sync with the input, without re-emitting.
    queueMicrotask(() => {
      const value = this.language();
      if (value)
        this.currentLanguage = Languages.find((x) => x.value === value);
    });
  }

  protected changeLanguage(language: LanguageModel | null): void {
    if (!language || language.value === this.currentLanguage?.value) return;
    this.currentLanguage = language;
    this._translateService.setDefaultLang(language.value);
    this.languageChange.emit(language.value);
  }
}
